import { LineMaximizeAlt03 } from '@qr-front/icons/line-maximize-alt-03'
import { useAtom, useAtomValue } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'

import { PrimaryAndSecondaryColorFields } from '@/components/PrimaryAndSecondaryColorFields'
import { TTranslationFunction } from '@/types'

import { TGeneratorFormSection } from '../../components/GeneratorFormSection'
import { FRAME_BLACK } from '../../qrFrames/qrFramesConstants'
import { TCornerDotStyle, TCornerFrameStyle } from '../../services/client'
import {
	DotDotIcon,
	DotFrameIcon,
	ExtraRoundedFrameIcon,
	SquareDotIcon,
	SquareFrameIcon,
} from './cornerIcons'
import { NoneIcon } from './NoneIcon'
import { qrBackgroundColorAtom } from './PatternDesignForm'
import { QrStyleField } from './QrStyleField'

const FRAME_OPTION_LIST = (t: TTranslationFunction) => [
	{
		value: undefined,
		Preview: NoneIcon,
		tooltip: t('generator__design_form__no_style_tooltip'),
	},
	{
		value: 'dot',
		Preview: DotFrameIcon,
		tooltip: t('generator__design_form__dot_frame_tooltip'),
	},
	{
		value: 'square',
		Preview: SquareFrameIcon,
		tooltip: t('generator__design_form__square_frame_tooltip'),
	},
	{
		value: 'extra-rounded',
		Preview: ExtraRoundedFrameIcon,
		tooltip: t('generator__design_form__extra_rounded_frame_tooltip'),
	},
]

export const cornerFrameStyleAtom = atomWithReset<TCornerFrameStyle>(undefined)
export const cornerFrameColorAtom = atomWithReset(FRAME_BLACK)
export const cornerDotColorAtom = atomWithReset(FRAME_BLACK)
export const cornerDotStyleAtom = atomWithReset<TCornerDotStyle>(undefined)

const FrameStyleField = () => {
	const t = useTranslations()

	const [selectedOption, setSelectedOption] = useAtom(cornerFrameStyleAtom)
	const color = useAtomValue(cornerFrameColorAtom)
	const qrBackgroundColor = useAtomValue(qrBackgroundColorAtom)

	return (
		<QrStyleField
			designElement="corners"
			label={t('generator__design_form__corner_section__frame_label')}
			value={selectedOption === undefined ? 'none' : selectedOption}
			setValue={(value) =>
				setSelectedOption(
					value === 'none' ? undefined : (value as TCornerFrameStyle),
				)
			}
			optionList={FRAME_OPTION_LIST(t)}
			color={color}
			backgroundColor={qrBackgroundColor}
			className="tablet:w-1/2"
		/>
	)
}

const DOT_OPTION_LIST = (t: TTranslationFunction) => [
	{
		value: undefined,
		Preview: NoneIcon,
		tooltip: t('generator__design_form__no_style_tooltip'),
	},
	{
		value: 'dot',
		Preview: DotDotIcon,
		tooltip: t('generator__design_form__corner_dot_tooltip'),
	},
	{
		value: 'square',
		Preview: SquareDotIcon,
		tooltip: t('generator__design_form__corner_square_tooltip'),
	},
]

const DotStyleField = () => {
	const t = useTranslations()

	const [selectedOption, setSelectedOption] = useAtom(cornerDotStyleAtom)
	const dotColor = useAtomValue(cornerDotColorAtom)
	const qrBackgroundColor = useAtomValue(qrBackgroundColorAtom)

	return (
		<QrStyleField
			designElement="corners"
			label={t('generator__design_form__corner_section__dot_label')}
			value={selectedOption === undefined ? 'none' : selectedOption}
			setValue={(value) =>
				setSelectedOption(
					value === 'none' ? undefined : (value as TCornerDotStyle),
				)
			}
			optionList={DOT_OPTION_LIST(t)}
			color={dotColor}
			backgroundColor={qrBackgroundColor}
			className="tablet:w-1/2"
		/>
	)
}

export const CornersDesignForm = () => {
	const t = useTranslations()

	const [frameColor, setFrameColor] = useAtom(cornerFrameColorAtom)
	const [dotColor, setDotColor] = useAtom(cornerDotColorAtom)

	return (
		<div className="flex flex-col gap-6 tablet:gap-8">
			<h4 className="type-heading-4">
				{t('generator__design_form__corner_section__heading')}
			</h4>
			<div className="flex flex-col gap-6 tablet:flex-row tablet:gap-8 desktop:gap-18">
				<FrameStyleField />
				<DotStyleField />
			</div>
			<div className="rounded-10 bg-light-grey-70 p-4 tablet:p-6">
				<PrimaryAndSecondaryColorFields
					primaryColor={frameColor}
					setPrimaryColor={setFrameColor}
					primaryLabel={t(
						'generator__design_form__corner_section__frame_color',
					)}
					secondaryColor={dotColor}
					setSecondaryColor={setDotColor}
					secondaryLabel={t(
						'generator__design_form__corner_section__dot_color',
					)}
				/>
			</div>
		</div>
	)
}

export const useCornerDesignForm = (): TGeneratorFormSection => {
	const t = useTranslations()

	return {
		id: 'corner',
		title: t('generator__design_form__corner_section__title'),
		description: t(
			'generator__design_form__corner_section__description__changed',
		),
		// TODO: Temporary key. After deleting the old `...__description` from Lokalise, rename this __changed key back to `...__description`.
		icon: <LineMaximizeAlt03 />,
		Content: CornersDesignForm,
	}
}
